import DataGridTable from '@/components/shared/DataGridTable'
import { columns as orderColumns } from '@/components/pages/orders/columns'
import { columns as patientColumns } from '@/components/pages/patients/columns'
import { columns as userColumns } from '@/components/pages/users/columns'
import { buildTextFilterParam } from '@/lib/dataGridFilters'
import { toE164 } from '@/lib/phone'
import { useOrdersQuery } from '@/services/orders'
import { usePatientsQuery } from '@/services/patients'
import { useUsersQuery } from '@/services/users'
import { colors, fontSizes, fontWeights } from '@/theme'
import { Phone, PhoneDisabled, Search } from '@mui/icons-material'
import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material'
import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

const RESULTS_LIMIT = 10

function backNavState(phoneNumber: string) {
  return {
    backLabel: 'Back to incoming call',
    fallbackPath: `/ringcentral/incoming?number=${encodeURIComponent(phoneNumber)}`
  }
}

function SectionHeader({ title, count, viewAllHref }: { title: string; count: number; viewAllHref?: string }) {
  const navigate = useNavigate()

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '18px', fontWeight: fontWeights.semibold, color: colors.text.primary }}>
          {title}
        </Typography>
        <Box
          sx={{
            bgcolor: colors.grey[100],
            borderRadius: '6px',
            px: 1,
            py: 0.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '24px'
          }}
        >
          <Typography sx={{ fontSize: fontSizes.md, fontWeight: fontWeights.semibold, color: colors.text.secondary }}>
            {count}
          </Typography>
        </Box>
      </Box>
      {viewAllHref && (
        <Typography
          variant='link'
          onClick={() => navigate(viewAllHref)}
          sx={{ '&:hover': { textDecoration: 'underline' } }}
        >
          View all
        </Typography>
      )}
    </Box>
  )
}

function EmptySectionState({ label }: { label: string }) {
  return (
    <Paper variant='outlined' sx={{ p: 3, textAlign: 'center' }}>
      <Typography sx={{ fontSize: fontSizes.md, color: colors.text.muted }}>{label}</Typography>
    </Paper>
  )
}

function IncomingHeader({ displayNumber }: { displayNumber: string }) {
  return (
    <Paper variant='outlined' sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          bgcolor: colors.brand.primaryLight,
          color: colors.brand.primary,
          borderRadius: '50%',
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Phone />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.muted }}>Incoming call</Typography>
        <Typography sx={{ fontSize: '22px', fontWeight: fontWeights.semibold, color: colors.text.primary }}>
          {displayNumber}
        </Typography>
      </Box>
    </Paper>
  )
}

function MissingNumberNotice() {
  return (
    <Alert severity='info'>No phone number was provided. This page is opened by RingCentral when a call rings.</Alert>
  )
}

function InvalidNumberNotice({ rawNumber }: { rawNumber: string }) {
  return (
    <Alert severity='warning'>
      Could not parse <strong>{rawNumber}</strong> as a phone number. Please search manually from the dashboard.
    </Alert>
  )
}

function NoMatchNotice({ phoneNumber }: { phoneNumber: string }) {
  const navigate = useNavigate()

  return (
    <Paper variant='outlined' sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <PhoneDisabled sx={{ fontSize: 40, color: colors.text.muted }} />
      <Typography sx={{ fontSize: '18px', fontWeight: fontWeights.semibold, color: colors.text.primary }}>
        No match for {phoneNumber}
      </Typography>
      <Typography sx={{ fontSize: fontSizes.md, color: colors.text.muted, textAlign: 'center' }}>
        No patient, user, or order was found with this phone number. Ask the caller for their order number or email and
        search from the dashboard.
      </Typography>
      <Button variant='outlined' startIcon={<Search />} onClick={() => navigate('/')} sx={{ mt: 1 }}>
        Go to dashboard search
      </Button>
    </Paper>
  )
}

function PatientsResults({
  phoneNumber,
  patients,
  isFetching
}: {
  phoneNumber: string
  patients: ReturnType<typeof usePatientsQuery>['patients']
  isFetching: boolean
}) {
  const navigate = useNavigate()
  const count = patients.length
  const viewAllHref =
    count > 0
      ? `/patients?phoneFilter=${encodeURIComponent(buildTextFilterParam({ operator: 'equals', value: phoneNumber }))}`
      : undefined

  return (
    <Box>
      <SectionHeader title='Patients' count={count} viewAllHref={viewAllHref} />
      {count === 0 ? (
        <EmptySectionState label='No matching patients' />
      ) : (
        <DataGridTable
          compactMode
          rows={patients}
          columns={patientColumns}
          rowCount={count}
          loading={isFetching}
          onRowClick={params => navigate(`/patients/${params.id}`, { state: backNavState(phoneNumber) })}
        />
      )}
    </Box>
  )
}

function UsersResults({
  phoneNumber,
  users,
  isFetching
}: {
  phoneNumber: string
  users: ReturnType<typeof useUsersQuery>['users']
  isFetching: boolean
}) {
  const navigate = useNavigate()
  const count = users.length
  const viewAllHref =
    count > 0
      ? `/users?phoneFilter=${encodeURIComponent(buildTextFilterParam({ operator: 'equals', value: phoneNumber }))}`
      : undefined

  return (
    <Box>
      <SectionHeader title='Users' count={count} viewAllHref={viewAllHref} />
      {count === 0 ? (
        <EmptySectionState label='No matching users' />
      ) : (
        <DataGridTable
          compactMode
          rows={users}
          columns={userColumns}
          rowCount={count}
          loading={isFetching}
          onRowClick={params =>
            navigate(`/users/${encodeURIComponent(params.row.email)}`, { state: backNavState(phoneNumber) })
          }
        />
      )}
    </Box>
  )
}

function OrdersResults({
  phoneNumber,
  orders,
  isFetching
}: {
  phoneNumber: string
  orders: ReturnType<typeof useOrdersQuery>['orders']
  isFetching: boolean
}) {
  const navigate = useNavigate()
  const count = orders.length
  const viewAllHref =
    count > 0
      ? `/orders?shippingPhoneFilter=${encodeURIComponent(buildTextFilterParam({ operator: 'equals', value: phoneNumber }))}`
      : undefined

  return (
    <Box>
      <SectionHeader title='Orders' count={count} viewAllHref={viewAllHref} />
      {count === 0 ? (
        <EmptySectionState label='No matching orders' />
      ) : (
        <DataGridTable
          compactMode
          rows={orders}
          columns={orderColumns}
          rowCount={count}
          loading={isFetching}
          onRowClick={params => navigate(`/orders/${params.id}`, { state: backNavState(phoneNumber) })}
        />
      )}
    </Box>
  )
}

function Results({ phoneNumber }: { phoneNumber: string }) {
  const filterValue = buildTextFilterParam({ operator: 'equals', value: phoneNumber })

  const patients = usePatientsQuery({
    tableId: 'ringcentral-patients',
    params: { limit: RESULTS_LIMIT, phoneFilter: filterValue, skipCount: true }
  })
  const users = useUsersQuery({
    tableId: 'ringcentral-users',
    params: { limit: RESULTS_LIMIT, phoneFilter: filterValue, skipCount: true }
  })
  const orders = useOrdersQuery({
    tableId: 'ringcentral-orders',
    params: { limit: RESULTS_LIMIT, shippingPhoneFilter: filterValue, skipCount: true }
  })

  const isInitialLoading = patients.isLoading || users.isLoading || orders.isLoading

  if (isInitialLoading) {
    return <CircularProgress size={32} sx={{ mx: 'auto', display: 'block', my: 6 }} />
  }

  const totalRendered = patients.patients.length + users.users.length + orders.orders.length
  if (totalRendered === 0) {
    return <NoMatchNotice phoneNumber={phoneNumber} />
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <PatientsResults phoneNumber={phoneNumber} patients={patients.patients} isFetching={patients.isFetching} />
      <UsersResults phoneNumber={phoneNumber} users={users.users} isFetching={users.isFetching} />
      <OrdersResults phoneNumber={phoneNumber} orders={orders.orders} isFetching={orders.isFetching} />
    </Box>
  )
}

export default function RingCentralIncomingPage() {
  const [searchParams] = useSearchParams()
  const rawNumber = searchParams.get('number')?.trim() ?? ''
  const normalized = useMemo(() => toE164(rawNumber), [rawNumber])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <IncomingHeader displayNumber={normalized ?? rawNumber ?? '—'} />
      {!rawNumber ? (
        <MissingNumberNotice />
      ) : !normalized ? (
        <InvalidNumberNotice rawNumber={rawNumber} />
      ) : (
        <Results phoneNumber={normalized} />
      )}
    </Box>
  )
}
